import { useTranslate } from '@tolgee/react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { probeServerReachable } from '../../../hooks/useIsOnline';
import { Button } from '../../_atoms/Button';
import PoorInternetNotification from '../PoorInternetNotification';

/**
 * Shown on the live page when NextAuth's session went null while a lesson is running.
 *
 * Almost always a network blip rather than a real sign-out (see `useConfirmedSession`),
 * so this never navigates: the lesson keeps running behind it and `useConfirmedSession`'s
 * self-repair usually clears it within a few seconds. Only once a manual recheck comes
 * back empty do we offer a sign-in link — and that opens in a new tab so the teacher is
 * never pulled out of the presentation.
 */
const SessionInterruptedNotification = ({
  onRecheck,
}: {
  onRecheck: () => Promise<unknown>;
}) => {
  const { t } = useTranslate();
  const router = useRouter();
  const [isRechecking, setIsRechecking] = useState(false);
  const [isConfirmedSignedOut, setIsConfirmedSignedOut] = useState(false);

  const handleRecheck = async () => {
    setIsRechecking(true);
    try {
      // `getSession()` resolves to `null` on a network failure exactly as it does on a
      // real sign-out — the conflation this whole change exists to undo. It does not
      // throw, so a null alone proves nothing. Only claim the user is signed out when
      // the server was reachable and still had no session for them.
      const [result, isReachable] = await Promise.all([
        onRecheck(),
        probeServerReachable(),
      ]);
      setIsConfirmedSignedOut(!result && isReachable);
    } catch {
      // A failed recheck is a network problem, not proof of a sign-out — leave the
      // banner in its "we lost your sign-in" state so the user can try again.
    } finally {
      setIsRechecking(false);
    }
  };

  if (isConfirmedSignedOut) {
    return (
      <PoorInternetNotification
        type="error"
        message={t(
          'LiveSessionSignedOut',
          "You've been signed out. Your lesson is still running.",
        )}
        action={
          <Link
            href={`/login?redirect=${encodeURIComponent(router.asPath)}`}
            target="_blank"
          >
            <Button type="orange">{t('SignInAgain', 'Sign in again')}</Button>
          </Link>
        }
      />
    );
  }

  return (
    <PoorInternetNotification
      type="warning"
      message={t(
        'LiveSessionInterrupted',
        'We lost your sign-in. Your lesson is still running.',
      )}
      action={
        <Button type="orange" loading={isRechecking} onClick={handleRecheck}>
          {t('TryAgain', 'Try again')}
        </Button>
      }
    />
  );
};

export default SessionInterruptedNotification;
